import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Alert, Box, Collapse, IconButton, LinearProgress, Link, Stack, Typography} from '@mui/material';
import {getSearchSummary} from '../../../../../../../utils/api/segments';
import {useReportStore} from '../../../../../useReportStore';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// Markdown component overrides for consistent styling
const markdownComponents = {
    a: ({node, ...props}) => (
        <Link {...props} target="_blank" rel="noopener noreferrer"/>
    ),
    p: ({node, ...props}) => (
        <Typography variant="body1" sx={{mb: 1}} {...props} />
    ),
    ul: ({node, ...props}) => (
        <Box component="ul" sx={{pl: 2, mb: 1}} {...props} />
    ),
    ol: ({node, ...props}) => (
        <Box component="ol" sx={{pl: 2, mb: 1}} {...props} />
    ),
    li: ({node, ...props}) => (
        <Typography component="li" variant="body1" {...props} />
    ),
    code: ({node, inline, className, children, ...props}) => {
        if (inline) {
            return (
                <Box component="code"
                     sx={{backgroundColor: 'rgba(0,0,0,0.06)', px: 0.5, borderRadius: '4px'}} {...props}>
                    {children}
                </Box>
            );
        }
        return (
            <Box component="pre" sx={{backgroundColor: '#f6f8fa', p: 1, borderRadius: 1, overflow: 'auto'}}>
                <Box component="code" className={className} {...props}>{children}</Box>
            </Box>
        );
    }
};

/**
 * Displays AI-generated summary for the current search.
 */
export default function SearchSummary() {
    const searchId = useReportStore(s => s.searchId);
    const searchSummaries = useReportStore(s => s.searchSummaries);
    const setSearchSummary = useReportStore(s => s.setSearchSummary);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [summary, setSummary] = useState(null);
    const [isExpanded, setIsExpanded] = useState(false);

    const abortRef = useRef(null);
    const lastSearchIdRef = useRef(null);

    useEffect(() => {
        if (!searchId) {
            setSummary(null);
            setError(null);
            if (abortRef.current) abortRef.current.abort();
            return;
        }

        const cached = searchSummaries?.[searchId];
        if (cached) {
            setSummary(cached);
            setError(null);
            setLoading(false);
            lastSearchIdRef.current = searchId;
            return;
        }

        // Avoid re-fetch for the same searchId
        if (lastSearchIdRef.current === searchId) return;
        lastSearchIdRef.current = searchId;

        // Cancel previous request
        if (abortRef.current) abortRef.current.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        setLoading(true);
        setError(null);
        setSummary(null);

        getSearchSummary(searchId, {signal: controller.signal})
            .then((data) => {
                const text = typeof data === 'string' ? data : (data?.summary || data?.text || JSON.stringify(data));
                setSummary(text);
                setSearchSummary(searchId, text);
            })
            .catch((err) => {
                if (err?.name !== 'AbortError') {
                    setError(err?.message || 'Failed to load summary');
                }
            })
            .finally(() => {
                if (abortRef.current === controller) abortRef.current = null;
                setLoading(false);
            });

        return () => {
            if (abortRef.current === controller) {
                controller.abort();
                abortRef.current = null;
            }
        };
    }, [searchId, searchSummaries, setSearchSummary]);

    // Compute preview text
    const preview = useMemo(() => {
        if (!summary) return null;
        const firstPara = summary.split(/\n\s*\n/)[0] || summary;
        return firstPara.length > 300 ? firstPara.slice(0, 300) + '…' : firstPara;
    }, [summary]);

    if (!searchId) return null;

    return (
        <Stack
            p={2}
            spacing={1}
            bgcolor="white"
            sx={{
                position: 'relative',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    background: 'linear-gradient(90deg, #7C4DFF 0%, #00C6FF 100%)',
                },
            }}
        >
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                <Stack direction="row" spacing={1} alignItems="center" sx={{flex: 1}}>
                    <AutoAwesomeIcon fontSize="small" sx={{mr: 1, color: '#7C4DFF'}}/>
                    <Typography variant="subtitle2" color="text.secondary">
                        {loading || !summary ? 'Getting AI Summary' : 'AI Summary'}
                    </Typography>
                </Stack>

                {summary && (
                    <IconButton
                        size="small"
                        onClick={() => setIsExpanded(v => !v)}
                        aria-expanded={isExpanded}
                        aria-label={isExpanded ? 'Collapse summary' : 'Expand summary'}
                        sx={{transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 200ms'}}
                    >
                        <ExpandMoreIcon/>
                    </IconButton>
                )}
            </Stack>

            {(loading || !summary) && <LinearProgress/>}
            {error && <Alert severity="error">{error}</Alert>}

            {summary && !isExpanded && (
                <Typography variant="body2" color="text.secondary" sx={{mt: 1}}>
                    {preview}
                </Typography>
            )}

            {summary && (
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <Box sx={{mt: 1}}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                            {summary}
                        </ReactMarkdown>
                    </Box>
                </Collapse>
            )}
        </Stack>
    );
}
